import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { profileAPI, onboardingAPI, profileViewAPI } from '../services/api';
import ProfileCompletionMeter from '../components/ProfileCompletionMeter';
import './FemaleDashboard.css';

const FemaleDashboard = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [completionStatus, setCompletionStatus] = useState(null);
  const [profileViews, setProfileViews] = useState([]);
  const [stats, setStats] = useState({
    viewsToday: 0,
    viewsThisWeek: 0,
    totalViews: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load profile
      const profileRes = await profileAPI.getMyProfile();
      setProfile(profileRes.data);
      
      // Load completion status
      const completionRes = await onboardingAPI.getCompletionStatus();
      setCompletionStatus(completionRes.data);
      
      // Load profile views
      try {
        const viewsRes = await profileViewAPI.getMyProfileViews();
        setProfileViews(viewsRes.data.slice(0, 5)); // Get last 5 views
        
        // Calculate stats
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        
        const viewsToday = viewsRes.data.filter(v => new Date(v.viewedAt) >= today).length;
        const viewsThisWeek = viewsRes.data.filter(v => new Date(v.viewedAt) >= weekAgo).length;
        
        setStats({
          viewsToday,
          viewsThisWeek,
          totalViews: viewsRes.data.length
        });
      } catch (err) {
        console.error('Failed to load profile views:', err);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setLoading(false);
    }
  };

  const getTips = () => {
    if (!profile || !completionStatus) return [];
    
    const tips = [];
    const completion = completionStatus.percentage || 0;
    
    if (completion < 100) {
      tips.push({
        icon: '📸',
        title: 'Complete Your Profile',
        description: `Your profile is ${completion}% complete. Completed profiles get 10x more views!`,
        action: 'Complete Now',
        onClick: () => navigate('/profile')
      });
    }
    
    // Creator mode and paid video-call tips removed - creator mode disabled, calls are free
    
    if (stats.viewsToday === 0) {
      tips.push({
        icon: '🔥',
        title: 'Stay Active',
        description: 'Active profiles appear higher in search results. Browse profiles to increase visibility.',
        action: 'Browse Now',
        onClick: () => navigate('/discover')
      });
    }
    
    return tips;
  };

  if (loading) {
    return (
      <div className="female-dashboard">
        <div className="dashboard-loading">Loading your dashboard...</div>
      </div>
    );
  }

  const tips = getTips();

  return (
    <div className="female-dashboard">
      <div className="dashboard-header">
        <h1>Welcome back, {profile?.name || 'Beautiful'}! 💕</h1>
        <p className="dashboard-subtitle">Here's what's happening with your profile</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">👀</div>
          <div className="stat-content">
            <div className="stat-value">{stats.viewsToday}</div>
            <div className="stat-label">Views Today</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-value">{stats.viewsThisWeek}</div>
            <div className="stat-label">Views This Week</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">💬</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalViews}</div>
            <div className="stat-label">Total Profile Views</div>
          </div>
        </div>
        
        <div className="stat-card clickable" onClick={() => navigate('/inbox')}>
          <div className="stat-icon">✉️</div>
          <div className="stat-content">
            <div className="stat-value">Check</div>
            <div className="stat-label">Your Messages</div>
          </div>
        </div>
      </div>

      {/* Profile Completion */}
      {completionStatus && completionStatus.percentage < 100 && (
        <div className="dashboard-section">
          <h2>Profile Completion</h2>
          <ProfileCompletionMeter 
            percentage={completionStatus.percentage}
            tasks={completionStatus.tasks}
          />
        </div>
      )}

      {/* Tips Section */}
      {tips.length > 0 && (
        <div className="dashboard-section">
          <h2>Tips to Get More Requests</h2>
          <div className="tips-grid">
            {tips.map((tip, index) => (
              <div key={index} className="tip-card" onClick={tip.onClick}>
                <div className="tip-icon">{tip.icon}</div>
                <div className="tip-content">
                  <h3>{tip.title}</h3>
                  <p>{tip.description}</p>
                </div>
                <button className="tip-action">{tip.action}</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Profile Views */}
      {profileViews.length > 0 && (
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Recent Profile Views</h2>
            <button 
              className="view-all-btn"
              onClick={() => navigate('/profile-views')}
            >
              View All
            </button>
          </div>
          <div className="views-list">
            {profileViews.map((view, index) => (
              <div 
                key={index} 
                className="view-item"
                onClick={() => navigate(`/profile/${view.viewerUserId}`)}
              >
                <div className="view-avatar">
                  {view.viewerPhotoUrl ? (
                    <img src={view.viewerPhotoUrl} alt={view.viewerName} />
                  ) : (
                    <div className="avatar-placeholder">
                      {view.viewerName?.charAt(0) || '?'}
                    </div>
                  )}
                </div>
                <div className="view-info">
                  <div className="view-name">{view.viewerName || 'Anonymous'}</div>
                  <div className="view-time">{getTimeAgo(view.viewedAt)}</div>
                </div>
                <svg className="view-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="dashboard-section">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          <button className="action-btn" onClick={() => navigate('/profile')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
            </svg>
            Edit Profile
          </button>
          
          <button className="action-btn" onClick={() => navigate('/discover')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
              <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Browse Members
          </button>
          
          <button className="action-btn" onClick={() => navigate('/inbox')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H5C4.46957 17 3.96086 16.7893 3.58579 16.4142C3.21071 16.0391 3 15.5304 3 15V8C3 7.46957 3.21071 6.96086 3.58579 6.58579C3.96086 6.21071 4.46957 6 5 6H19C19.5304 6 20.0391 6.21071 20.4142 6.58579C20.7893 6.96086 21 7.46957 21 8V15Z" stroke="currentColor" strokeWidth="2"/>
              <path d="M3 8L12 13L21 8" stroke="currentColor" strokeWidth="2"/>
            </svg>
            Check Messages
          </button>
          
          {/* Creator Hub button removed - creator mode disabled in UI */}
        </div>
      </div>
    </div>
  );
};

const getTimeAgo = (timestamp) => {
  if (!timestamp) return 'Recently';
  
  const now = new Date();
  const viewTime = new Date(timestamp);
  const diffMs = now - viewTime;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return viewTime.toLocaleDateString();
};

export default FemaleDashboard;
